import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X, Calendar } from 'lucide-react';
import { campaignAPI } from '../services/api';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 1400px;
  max-height: 90vh;
  overflow: auto;
  position: relative;
  font-family: 'Prompt', sans-serif;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #E5E7EB;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-size: 28px;
  font-weight: 400;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #9CA3AF;

  &:hover {
    color: #6B7280;
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const ImageUploadSection = styled.div`
  margin-bottom: 2rem;
`;

const ImageUploadInput = styled.input`
  display: none;
`;

const ImageUploadButton = styled.button`
  background-color: #F9FAFB;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  padding: 0.75rem 1.5rem;
  font-size: 14px;
  cursor: pointer;
  color: #374151;
  font-weight: 400;

  &:hover {
    background-color: #F3F4F6;
  }
`;

const ImagePreview = styled.img`
  margin-top: 1rem;
  max-width: 200px;
  max-height: 200px;
  border-radius: 6px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const FormField = styled.div``;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #404E67;
  display: block;
  margin-bottom: 0.5rem;
`;

const RequiredStar = styled.span`
  color: #EF4444;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 14px;
  background-color: white;

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const DateInputContainer = styled.div`
  position: relative;
`;

const DateInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  padding-right: 2.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }

  &::-webkit-calendar-picker-indicator {
    opacity: 0;
    position: absolute;
    right: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
`;

const CalendarIcon = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9CA3AF;
  pointer-events: none;
`;

const EmptyListSection = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: #404E67;
  font-size: 16px;
  font-weight: 400;
  background-color: #F3F4F6;
  border-radius: 6px;
  margin-bottom: 2rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 1rem;
  margin-top: 2rem;
`;

const CancelButton = styled.button`
  background-color: white;
  color: #6B7280;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  padding: 0.75rem 1.5rem;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: #F9FAFB;
  }
`;

const SubmitButton = styled.button`
  background-color: #00B5B8;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.75rem 3rem;
  font-size: 14px;
  cursor: pointer;
  flex: 1;

  &:hover {
    background-color: #009A9D;
  }
`;

const ExamineeGroupModal = ({ isOpen, onClose, onSave, memberData }) => {
  const [formData, setFormData] = useState({
    prefix: '',
    firstName: '',
    lastName: '',
    username: '',
    password: '',
    email: '',
    phone: '',
    gender: 'ชาย',
    idCard: '',
    birthDate: '',
    lineUid: '',
    company: '',
    position: '',
    job: '',
    isCompanyAdmin: false
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [companiesList, setCompaniesList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchCompanies();
    }
  }, [isOpen]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await campaignAPI.getList(1, '');
      if (response.result && response.list && Array.isArray(response.list)) {
        const companies = response.list
          .filter(item => item.name && item.name !== '-')
          .map(item => ({
            id: item.id,
            name: item.name
          }));

        const uniqueCompanies = companies.filter((company, index, self) =>
          index === self.findIndex(c => c.name === company.name)
        );

        setCompaniesList(uniqueCompanies);
      }
    } catch (err) {
      console.error('Error fetching companies:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (memberData) {
      const primaryCampaignId = String(memberData.memberCampaign?.[0]?.campaignid || '');

      setFormData({
        prefix: memberData.nameprefix || '',
        firstName: memberData.name || '',
        lastName: memberData.lastname || '',
        username: memberData.username || '',
        password: '',
        email: memberData.email || '',
        phone: memberData.tel || '',
        gender: memberData.gender === '1' ? 'ชาย' : (memberData.gender === '2' ? 'หญิง' : 'อื่นๆ'),
        idCard: memberData.nid || '',
        birthDate: memberData.birthday || '',
        lineUid: memberData.lineuid || '',
        company: primaryCampaignId,
        position: memberData.position || '',
        job: memberData.job || '',
        isCompanyAdmin: memberData.isCompanyAdmin || false
      });

      if (memberData.image) {
        setSelectedImage(memberData.image);
      }
    } else {
      setFormData({
        prefix: '',
        firstName: '',
        lastName: '',
        username: '',
        password: '',
        email: '',
        phone: '',
        gender: 'ชาย',
        idCard: '',
        birthDate: '',
        lineUid: '',
        company: '',
        position: '',
        job: '',
        isCompanyAdmin: false
      });
      setSelectedImage(null);

    }
  }, [memberData, isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };


  const handleSubmit = () => {
    if (onSave) {
      const apiData = {
        username: formData.username,
        password: formData.password,
        nameprefix: formData.prefix,
        name: formData.firstName,
        lastname: formData.lastName,
        email: formData.email || '',
        tel: formData.phone || '0',
        nid: formData.idCard || '0',
        birthday: formData.birthDate || '2000-01-01',
        gender: formData.gender === 'ชาย' ? '1' : (formData.gender === 'หญิง' ? '2' : '0'),
        image: '',
        lineuid: formData.lineUid || '',
        campaignid: formData.company || '0',
        level: '2',
        position: formData.position || '',
        job: formData.job || ''
      };

      if (memberData && memberData.id) {
        apiData.id = memberData.id;
      }

      console.log('📤 Sending member data:', apiData);
      console.log('📤 Selected company ID:', formData.company);

      onSave(apiData);
    } else {
      onClose();
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(URL.createObjectURL(file));
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{memberData ? 'แก้ไขสมาชิก' : 'จัดการกลุ่มผู้รับการตรวจ'}</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <FormGrid>
            <FormField>
              <Label>
                คำนำหน้าชื่อ <RequiredStar>*</RequiredStar>
              </Label>
              <Input
                type="text"
                value={formData.prefix}
                onChange={(e) => handleInputChange('prefix', e.target.value)}
              />
            </FormField>

            <FormField>
              <Label>
                ชื่อ <RequiredStar>*</RequiredStar>
              </Label>
              <Input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
              />
            </FormField>

            <FormField>
              <Label>
                นามสกุล <RequiredStar>*</RequiredStar>
              </Label>
              <Input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
              />
            </FormField>

            <FormField>
              <Label>
                ชื่อผู้ใช้ <RequiredStar>*</RequiredStar>
              </Label>
              <Input
                type="text"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
              />
            </FormField>

            <FormField>
              <Label>
                รหัสผ่าน <RequiredStar>*</RequiredStar>
              </Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
              />
            </FormField>

            <FormField>
              <Label>อีเมล</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </FormField>

            <FormField>
              <Label>
                โทร <RequiredStar>*</RequiredStar>
              </Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </FormField>

            <FormField>
              <Label>เพศ</Label>
              <Select
                value={formData.gender}
                onChange={(e) => handleInputChange('gender', e.target.value)}
              >
                <option value="ชาย">ชาย</option>
                <option value="หญิง">หญิง</option>
                <option value="อื่นๆ">อื่นๆ</option>
              </Select>
            </FormField>

            <FormField>
              <Label>
                เลขบัตรประชาชน/เลขบัตรประกันสังคม/passport สำหรับชาวต่างชาติ<RequiredStar>*</RequiredStar>
              </Label>
              <Input
                type="text"
                value={formData.idCard}
                onChange={(e) => handleInputChange('idCard', e.target.value)}
              />
            </FormField>

            <FormField>
              <Label>วันเกิด</Label>
              <DateInputContainer>
                <DateInput
                  type="date"
                  value={formData.birthDate}
                  onChange={(e) => handleInputChange('birthDate', e.target.value)}
                />
                <CalendarIcon>
                  <Calendar size={16} />
                </CalendarIcon>
              </DateInputContainer>
            </FormField>

            <FormField>
              <Label>uid Line application</Label>
              <Input
                type="text"
                value={formData.lineUid}
                onChange={(e) => handleInputChange('lineUid', e.target.value)}
              />
            </FormField>

            <FormField>
              <Label>&nbsp;</Label>
              <div style={{ display: 'flex', alignItems: 'center', paddingTop: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
                  <input
                    type="checkbox"
                    checked={formData.isCompanyAdmin}
                    onChange={(e) => handleInputChange('isCompanyAdmin', e.target.checked)}
                    style={{ width: '1rem', height: '1rem', cursor: 'pointer', accentColor: '#00B5B8' }}
                  />
                  admin บริษัท
                </label>
              </div>
            </FormField>

            <FormField>
              <Label>บริษัท</Label>
              <select
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  color: formData.company ? '#374151' : '#9CA3AF',
                }}
              >
                <option value="">
                  {loading ? 'กำลังโหลด...' : 'เลือกบริษัท'}
                </option>
                {companiesList.map(company => (
                  <option key={company.id} value={String(company.id)}>
                    {company.name}
                  </option>
                ))}
              </select>
            </FormField>
          </FormGrid>

          <ButtonContainer>
            <CancelButton onClick={onClose}>
              ยกเลิก
            </CancelButton>
            <SubmitButton onClick={handleSubmit}>
              {memberData ? 'บันทึก' : 'เพิ่ม'}
            </SubmitButton>
          </ButtonContainer>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ExamineeGroupModal;
